import { Form } from "@/shared/form/Form";
import { FormDefinition, FormFieldType } from "@/shared/form/FormDefinition";

import {
  DocumentListOption,
  defaultDocumentList,
  documentListFrom
} from "../models/DocumentListOptions";

/** The single field this sub-form keeps the whole option list in. */
export const DOCUMENT_LIST_FIELD = "documentList";

/**
 * `content.documentList` — Dokumentenliste of the Bauwerksbuch report.
 *
 * Unlike every other sub-form, this one's payload is a bare **array**, not an
 * object of named fields: the renderer reads `content.documentList` as the
 * ordered option list. The sub-form therefore keeps the whole list in a single
 * field and maps between that field and the array at the two boundaries the
 * report editor uses:
 *
 *  - `getData()` returns the array itself, so the parent form serializes
 *    `documentList: [...]` instead of `documentList: { … }`;
 *  - `setFieldValue()` accepts the *index* addressing the editor uses when it
 *    applies a loaded payload entry by entry, and merges that entry into the
 *    list.
 *
 * Both directions normalize against the fixed option catalog, so neither a
 * stale payload nor the widget can change the option set, its order, its
 * labels, or add a specification to "Keine".
 */
export class BauwerksbuchDocumentListForm extends Form<DocumentListOption[]> {
  protected definition: FormDefinition<DocumentListOption[]> = {
    [DOCUMENT_LIST_FIELD]: {
      label: "Dokumentenliste",
      type: FormFieldType.MultiSelect,
      required: false,
      defaultValue: defaultDocumentList()
    }
  };

  /** The current list, always the complete catalog in catalog order. */
  public get entries(): DocumentListOption[] {
    return documentListFrom(this.data[DOCUMENT_LIST_FIELD]?.value);
  }

  public getData(): DocumentListOption[] {
    return this.entries;
  }

  public setFieldValue(
    fieldName: string,
    value: unknown,
    skipValidation = false,
    userInput = true
  ) {
    const index = Number(fieldName);

    // a loaded payload arrives entry by entry, addressed by its array index
    if (!isNaN(index) && index === Math.floor(index)) {
      return super.setFieldValue(
        DOCUMENT_LIST_FIELD,
        this.mergedEntry(index, value),
        skipValidation,
        userInput
      );
    }

    return super.setFieldValue(
      fieldName,
      fieldName === DOCUMENT_LIST_FIELD ? documentListFrom(value) : value,
      skipValidation,
      userInput
    );
  }

  private mergedEntry(index: number, value: unknown): DocumentListOption[] {
    const entries = this.entries;
    const loaded = (value ?? {}) as Partial<DocumentListOption>;

    // an entry naming its own option wins over its position, so a reordered
    // payload still lands on the right option; an unknown option is dropped
    const position =
      loaded.key === undefined
        ? index
        : entries.findIndex(option => option.key === loaded.key);

    if (position < 0 || position >= entries.length) {
      return entries;
    }

    const current = entries[position];
    const merged: DocumentListOption = {
      ...current,
      checked: loaded.checked === true
    };

    if (current.specification !== undefined) {
      merged.specification =
        typeof loaded.specification === "string" ? loaded.specification : "";
    }

    entries[position] = merged;

    return entries;
  }
}
